// Ported from the native app's ChartIndicators.swift.

import type { OHLCPoint } from "./market-data.js";

export interface IndicatorPoint {
  time: number; // epoch ms
  value: number;
}

export type IndicatorKind = "sma20" | "sma50" | "sma200" | "ema20" | "ema50" | "vwap" | "bollingerBands" | "volume" | "rsi" | "macd" | "atr" | "stoch" | "adx";
export const indicatorKinds: IndicatorKind[] = ["sma20", "sma50", "sma200", "ema20", "ema50", "vwap", "bollingerBands", "volume", "rsi", "macd", "atr", "stoch", "adx"];

export const indicatorLabel: Record<IndicatorKind, string> = {
  sma20: "SMA 20",
  sma50: "SMA 50",
  sma200: "SMA 200",
  ema20: "EMA 20",
  ema50: "EMA 50",
  vwap: "VWAP",
  bollingerBands: "Bollinger Bands",
  volume: "Volume",
  rsi: "RSI 14",
  macd: "MACD 12·26·9",
  atr: "ATR 14",
  stoch: "Stochastic",
  adx: "ADX 14",
};

export const indicatorColor: Record<IndicatorKind, string> = {
  sma20: "#ffd60a",
  sma50: "#ff9f0a",
  sma200: "#ff375f",
  ema20: "#64d2ff",
  ema50: "#ffd60a",
  vwap: "#bf5af2",
  bollingerBands: "#0a84ff",
  volume: "#8e8e93",
  rsi: "#32ade6",
  macd: "#34c759",
  atr: "#ac8e68",
  stoch: "#5e5ce6",
  adx: "#ff9f0a",
};

/**
 * Simple moving average — the average close over the trailing `period`
 * candles. The first `period - 1` candles have no value yet, so the series
 * starts partway into the chart, same convention TradingView uses.
 */
export function simpleMovingAverage(candles: OHLCPoint[], period: number): IndicatorPoint[] {
  if (candles.length < period || period <= 0) return [];
  const result: IndicatorPoint[] = [];
  let windowSum = 0;
  for (let i = 0; i < period; i++) windowSum += candles[i]!.close;
  result.push({ time: candles[period - 1]!.date.getTime(), value: windowSum / period });
  for (let i = period; i < candles.length; i++) {
    windowSum += candles[i]!.close - candles[i - period]!.close;
    result.push({ time: candles[i]!.date.getTime(), value: windowSum / period });
  }
  return result;
}

const dayKeyFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Volume-weighted average price, cumulative from the start of the loaded
 * range, or reset at the start of each Eastern-time trading day for
 * intraday charts. Bars with zero reported volume are skipped rather than
 * pulling the average toward their price with zero weight.
 */
export function vwapSeries(candles: OHLCPoint[], resetDaily: boolean): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  let cumulativePriceVolume = 0;
  let cumulativeVolume = 0;
  let currentDay: string | null = null;

  for (const candle of candles) {
    if (resetDaily) {
      const day = dayKeyFormatter.format(candle.date);
      if (day !== currentDay) {
        currentDay = day;
        cumulativePriceVolume = 0;
        cumulativeVolume = 0;
      }
    }
    if (candle.volume <= 0) continue;
    const typicalPrice = (candle.high + candle.low + candle.close) / 3;
    cumulativePriceVolume += typicalPrice * candle.volume;
    cumulativeVolume += candle.volume;
    if (cumulativeVolume <= 0) continue;
    result.push({ time: candle.date.getTime(), value: cumulativePriceVolume / cumulativeVolume });
  }
  return result;
}

export interface BollingerBandPoint {
  time: number;
  lower: number;
  middle: number;
  upper: number;
}

/**
 * Classic 20-period SMA +/- 2 standard deviations across the whole series —
 * a full band to plot as a chart overlay, not just today's reading.
 */
export function bollingerBandSeries(candles: OHLCPoint[], period = 20, multiplier = 2): BollingerBandPoint[] {
  if (candles.length < period || period <= 0) return [];
  const closes = candles.map((c) => c.close);
  const points: BollingerBandPoint[] = [];
  for (let i = period - 1; i < candles.length; i++) {
    let mean = 0;
    for (let j = i - period + 1; j <= i; j++) mean += closes[j]!;
    mean /= period;
    let variance = 0;
    for (let j = i - period + 1; j <= i; j++) variance += (closes[j]! - mean) ** 2;
    variance /= period;
    const stdDev = Math.sqrt(variance);
    points.push({
      time: candles[i]!.date.getTime(),
      lower: mean - multiplier * stdDev,
      middle: mean,
      upper: mean + multiplier * stdDev,
    });
  }
  return points;
}

/**
 * Wilder's RSI: seeded with the simple average of the first `period`
 * gains/losses, then Wilder-smoothed. A flat stretch (no gains, no
 * losses) reads 50 — momentum is genuinely neutral, not undefined.
 */
export function rsiSeries(candles: OHLCPoint[], period = 14): IndicatorPoint[] {
  if (candles.length <= period || period <= 0) return [];
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const change = candles[i]!.close - candles[i - 1]!.close;
    if (change >= 0) avgGain += change;
    else avgLoss -= change;
  }
  avgGain /= period;
  avgLoss /= period;

  const rsi = () => {
    if (avgGain === 0 && avgLoss === 0) return 50;
    if (avgLoss === 0) return 100;
    return 100 - 100 / (1 + avgGain / avgLoss);
  };

  const points: IndicatorPoint[] = [{ time: candles[period]!.date.getTime(), value: rsi() }];
  for (let i = period + 1; i < candles.length; i++) {
    const change = candles[i]!.close - candles[i - 1]!.close;
    avgGain = (avgGain * (period - 1) + Math.max(change, 0)) / period;
    avgLoss = (avgLoss * (period - 1) + Math.max(-change, 0)) / period;
    points.push({ time: candles[i]!.date.getTime(), value: rsi() });
  }
  return points;
}

/** EMA over closes, SMA-seeded (the TradingView convention). Internal to
 * MACD but exported for tests. Index-aligned with `candles`; the first
 * `period - 1` slots are null. */
export function emaValues(candles: OHLCPoint[], period: number): Array<number | null> {
  const out: Array<number | null> = new Array(candles.length).fill(null);
  if (candles.length < period || period <= 0) return out;
  let seed = 0;
  for (let i = 0; i < period; i++) seed += candles[i]!.close;
  let prev = seed / period;
  out[period - 1] = prev;
  const k = 2 / (period + 1);
  for (let i = period; i < candles.length; i++) {
    prev = candles[i]!.close * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

export interface MACDPoint {
  time: number;
  macd: number;
  signal: number;
  histogram: number;
}

/**
 * MACD: EMA(fast) − EMA(slow), with an EMA(signalPeriod) signal line over
 * the MACD values (also SMA-seeded) and histogram = MACD − signal. Points
 * start once the signal line exists.
 */
export function macdSeries(candles: OHLCPoint[], fast = 12, slow = 26, signalPeriod = 9): MACDPoint[] {
  if (candles.length < slow + signalPeriod - 1) return [];
  const fastEma = emaValues(candles, fast);
  const slowEma = emaValues(candles, slow);
  const macdLine: number[] = [];
  const macdTimes: number[] = [];
  for (let i = slow - 1; i < candles.length; i++) {
    macdLine.push(fastEma[i]! - slowEma[i]!);
    macdTimes.push(candles[i]!.date.getTime());
  }

  let seed = 0;
  for (let i = 0; i < signalPeriod; i++) seed += macdLine[i]!;
  let signal = seed / signalPeriod;
  const k = 2 / (signalPeriod + 1);
  const points: MACDPoint[] = [
    {
      time: macdTimes[signalPeriod - 1]!,
      macd: macdLine[signalPeriod - 1]!,
      signal,
      histogram: macdLine[signalPeriod - 1]! - signal,
    },
  ];
  for (let i = signalPeriod; i < macdLine.length; i++) {
    signal = macdLine[i]! * k + signal * (1 - k);
    points.push({ time: macdTimes[i]!, macd: macdLine[i]!, signal, histogram: macdLine[i]! - signal });
  }
  return points;
}

/** EMA as an IndicatorPoint series (chart overlays / strategy operands). */
export function emaSeries(candles: OHLCPoint[], period: number): IndicatorPoint[] {
  const values = emaValues(candles, period);
  const points: IndicatorPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    const v = values[i];
    if (v != null) points.push({ time: candles[i]!.date.getTime(), value: v });
  }
  return points;
}

/**
 * Wilder's Average True Range: true range = max(high−low, |high−prevClose|,
 * |low−prevClose|), seeded with the simple mean of the first `period` TRs,
 * then Wilder-smoothed. The volatility yardstick behind stop distances.
 */
export function atrSeries(candles: OHLCPoint[], period = 14): IndicatorPoint[] {
  if (candles.length <= period || period <= 0) return [];
  const trs: number[] = [];
  for (let i = 1; i < candles.length; i++) {
    const c = candles[i]!;
    const prevClose = candles[i - 1]!.close;
    trs.push(Math.max(c.high - c.low, Math.abs(c.high - prevClose), Math.abs(c.low - prevClose)));
  }
  let atr = trs.slice(0, period).reduce((a, b) => a + b, 0) / period;
  const points: IndicatorPoint[] = [{ time: candles[period]!.date.getTime(), value: atr }];
  for (let i = period; i < trs.length; i++) {
    atr = (atr * (period - 1) + trs[i]!) / period;
    points.push({ time: candles[i + 1]!.date.getTime(), value: atr });
  }
  return points;
}

export interface StochasticPoint {
  time: number;
  k: number; // %K, 0..100
  d: number; // %D = SMA(%K, smoothD)
}

/** Fast Stochastic: %K = 100·(close − lowestLow_n)/(highestHigh_n − lowestLow_n),
 * %D = SMA(%K, smoothD). A degenerate window (flat) reads 50. */
export function stochasticSeries(candles: OHLCPoint[], period = 14, smoothD = 3): StochasticPoint[] {
  if (candles.length < period) return [];
  const kByIndex: Array<{ time: number; k: number }> = [];
  for (let i = period - 1; i < candles.length; i++) {
    let hi = -Infinity;
    let lo = Infinity;
    for (let j = i - period + 1; j <= i; j++) {
      hi = Math.max(hi, candles[j]!.high);
      lo = Math.min(lo, candles[j]!.low);
    }
    const span = hi - lo;
    const k = span > 0 ? (100 * (candles[i]!.close - lo)) / span : 50;
    kByIndex.push({ time: candles[i]!.date.getTime(), k });
  }
  const out: StochasticPoint[] = [];
  for (let i = 0; i < kByIndex.length; i++) {
    if (i < smoothD - 1) continue;
    let sum = 0;
    for (let j = i - smoothD + 1; j <= i; j++) sum += kByIndex[j]!.k;
    out.push({ time: kByIndex[i]!.time, k: kByIndex[i]!.k, d: sum / smoothD });
  }
  return out;
}

/**
 * Wilder's ADX: directional movement (+DM/−DM) and true range, each
 * Wilder-smoothed, give +DI/−DI; DX = 100·|+DI−−DI|/(+DI+−DI); ADX is the
 * Wilder-smoothed DX. Reads trend STRENGTH (0..100), not direction —
 * classic "only trade when ADX > 25". Needs ~2·period bars to settle.
 */
export function adxSeries(candles: OHLCPoint[], period = 14): IndicatorPoint[] {
  if (candles.length < 2 * period) return [];
  const tr: number[] = [];
  const plusDM: number[] = [];
  const minusDM: number[] = [];
  for (let i = 1; i < candles.length; i++) {
    const c = candles[i]!;
    const p = candles[i - 1]!;
    const up = c.high - p.high;
    const down = p.low - c.low;
    plusDM.push(up > down && up > 0 ? up : 0);
    minusDM.push(down > up && down > 0 ? down : 0);
    tr.push(Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close)));
  }
  // Wilder-smoothed sums seeded with the first `period` simple sums.
  let trS = tr.slice(0, period).reduce((a, b) => a + b, 0);
  let pS = plusDM.slice(0, period).reduce((a, b) => a + b, 0);
  let mS = minusDM.slice(0, period).reduce((a, b) => a + b, 0);
  const dx: Array<{ idx: number; value: number }> = [];
  for (let i = period; i < tr.length; i++) {
    trS = trS - trS / period + tr[i]!;
    pS = pS - pS / period + plusDM[i]!;
    mS = mS - mS / period + minusDM[i]!;
    const plusDI = trS > 0 ? (100 * pS) / trS : 0;
    const minusDI = trS > 0 ? (100 * mS) / trS : 0;
    const denom = plusDI + minusDI;
    dx.push({ idx: i + 1, value: denom > 0 ? (100 * Math.abs(plusDI - minusDI)) / denom : 0 });
  }
  if (dx.length < period) return [];
  const points: IndicatorPoint[] = [];
  let adx = dx.slice(0, period).reduce((a, b) => a + b.value, 0) / period;
  points.push({ time: candles[dx[period - 1]!.idx]!.date.getTime(), value: adx });
  for (let i = period; i < dx.length; i++) {
    adx = (adx * (period - 1) + dx[i]!.value) / period;
    points.push({ time: candles[dx[i]!.idx]!.date.getTime(), value: adx });
  }
  return points;
}
